import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import startCase from 'lodash/startCase';
import {
  AppContainer,
  BuildEnv,
  EnrichedAssetHoldings,
  getRuntimeEnv,
  RemoveNullable,
  useAppDialog,
  useEnvironmentConfig,
  useMarkets,
  UseMarketType,
  useTrackEvent,
} from 'common';
import { API } from '@xbto/api-client';
import { AppStore, DataStore } from '~/store';
import { DashboardHeader } from '~/components/dashboard/dashboard-header';
import {
  useUrlParams,
  QueryParams,
} from '~/components/shared/url-params/url-params';
import { AppLoader } from '~/components/app-loader';
import { MarketsColumn } from '~/config';
import { APP_ROUTES } from '~/routes';
import { WorkflowPortfolioRowButtons } from '~/components/workflows/row-buttons';
import { DASHBOARD_SELECTED_DIALOG_TYPE } from '~/store/types';
import { AccountSelector } from '~/components/workflows/account-selector';
import { GenericError } from '~/components/generic-error';
import {
  SearchEmpty,
  MarketList,
  MarketListHeader,
  MarketTableItemFiat,
  MarketTableItemBond,
  MarketTableItemFund,
  MarketTableItemCrypto,
  SearchInput,
  Pagination,
  MarketTableHeader,
  PageContainer,
  TabItemSwitcher,
  Tabs,
  useBreakpoints,
  type GetBuildEnvType,
} from '@xbto/universal-components';
import { useBeginWorkflow } from '~/hooks/use-begin-workflow';
import { useExperience } from '~/hooks/use-experience';
import { useCtrlClickNavigation } from '~/hooks/use-ctrl-click-navigation';

enum QUERY_PARAMS {
  COLUMN = 'column',
  DIRECTION = 'direction',
  ASSET_TYPE = 'assetType',
  ASSET_CATEGORY = 'assetCategory',
}

/**
 * Table components
 */
type OnWorkflowSelected = (
  type: DASHBOARD_SELECTED_DIALOG_TYPE,
  asset: EnrichedAssetHoldings
) => void;

interface AssetWorkflowActionProps {
  assetCode: string;
  onWorkflowSelected: OnWorkflowSelected;
  placement: 'top' | 'bottom';
}

const AssetWorkflowAction = ({
  assetCode,
  onWorkflowSelected,
  placement = 'bottom',
}: AssetWorkflowActionProps) => {
  const allowedPortfolioActions = DataStore.useStoreState(
    s => s.portfolio.allowedActions
  );
  return (
    <WorkflowPortfolioRowButtons
      assetCode={assetCode}
      onWorkflowSelected={onWorkflowSelected}
      placement={placement}
      allowedActions={allowedPortfolioActions}
    />
  );
};

type TableProps = PropsWithChildren & {
  empty: boolean;
  loading?: boolean;
  rounded?: boolean;
};

function Table({ empty, loading = false, rounded, ...rest }: TableProps) {
  return (
    <div
      {...rest}
      className={cx(
        'relative flex flex-col border-1 rounded-b-lg overflow-y-hidden',
        {
          'rounded-lg': rounded,
          'border-b-0': !empty && !loading, // overflow needed to avoid double border
          'min-h-200': loading,
        }
      )}
    />
  );
}

type TableRowsProps = Pick<
  TableDetailsProps,
  | 'activeAssetType'
  | 'data'
  | 'hasNoData'
  | 'header'
  | 'isLoading'
  | 'showCouponColumn'
  | 'showYieldColumn'
> &
  GetBuildEnvType;

const TableRows = memo(
  ({
    isLoading,
    data,
    hasNoData,
    header,
    activeAssetType,
    showCouponColumn,
    showYieldColumn,
    getBuildEnv,
  }: TableRowsProps) => {
    const { breakpoints } = useBreakpoints();
    const navigate = useNavigate();
    const trackEvent = useTrackEvent();
    const setDashboardSelectedDialogType = AppStore.useStoreActions(
      _ => _.setDashboardSelectedDialogType
    );
    const { showDialog, hideDialog } = useAppDialog();
    const ctrlClickNavigation = useCtrlClickNavigation();

    const handlePressAsset = useCallback(
      (currencyCode: string) => {
        // TODO(Benoit): Integrate `ctrlClickNavigation`
        trackEvent('markets-funds_row-item_clicked');
        navigate(`${APP_ROUTES.AUTH_ASSET}/${currencyCode}`);
      },
      [navigate, trackEvent]
    );

    const beginWorkflow = useBeginWorkflow();

    const onWorkflowSelected = useCallback(
      (
        dialogType: DASHBOARD_SELECTED_DIALOG_TYPE,
        assetHoldings: EnrichedAssetHoldings
      ) => {
        if (!dialogType) {
          navigate(APP_ROUTES.AUTH_ASSET + `/${assetHoldings?.currency?.code}`);
          return;
        }

        const accounts = assetHoldings.accounts;
        if (accounts.length === 1) {
          beginWorkflow(
            dialogType,
            accounts[0],
            accounts[0].account?.accountId
          );
          return;
        }

        showDialog(
          <AccountSelector
            workflowType={dialogType}
            onClose={hideDialog}
            onNext={async account => {
              hideDialog();
              const asset = accounts.find(
                acc => acc.account?.accountId === account.account?.accountId
              );
              beginWorkflow(dialogType, asset, account.account?.accountId);
            }}
          />
        );
      },
      [showDialog, hideDialog, setDashboardSelectedDialogType, beginWorkflow]
    );

    if (isLoading) {
      return (
        <AppLoader spinnerTop="104px" isFixed={false} bgColor="transparent" />
      );
    }
    if (hasNoData) {
      return (
        <SearchEmpty
          title="No assets available"
          message="No assets found under this search term, please try another term."
        />
      );
    }

    // TODO(Benoit): This is supposed to happen at XS breakpoint, but current Table implementation
    // does not work well under 768px.
    // This will be fixed when we migrate to the new Table component
    if (breakpoints.LESS_THAN_S) {
      return (
        <MarketList
          data={data?.rows}
          assetType={activeAssetType}
          onPressAsset={handlePressAsset}
          ListHeaderComponent={header}
          getBuildEnv={getBuildEnv}
        />
      );
    }

    return (
      <>
        {header}
        {data?.rows?.map((asset, index, arr) => {
          const isGreaterThanHalfItems = index > arr.length / 2;
          const itemProps = {
            apy: asset.apy,
            code: asset.code,
            displayCode: asset.displayCode,
            name: asset.name,
            trending: asset.isTrending,
            onPress: (_, event: React.MouseEvent) => {
              trackEvent('markets-funds_row-item_clicked');
              const path = `${APP_ROUTES.AUTH_ASSET}/${asset.code}`;
              if (!ctrlClickNavigation(event, path)) {
                navigate(path);
              }
            },
          };

          const actions = (
            <AssetWorkflowAction
              assetCode={asset.code}
              onWorkflowSelected={onWorkflowSelected}
              placement={isGreaterThanHalfItems ? 'top' : 'bottom'}
            />
          );

          if (
            activeAssetType === API.MarketAssetType.Fiat &&
            asset.data?.fiatMarketDetail
          ) {
            return (
              <MarketTableItemFiat
                key={asset.code}
                {...itemProps}
                currencyCode={asset.code}
                actions={actions}
                data={asset.data.fiatMarketDetail}
              />
            );
          }

          if (
            activeAssetType === API.MarketAssetType.Bond &&
            asset.data?.bondMarketDetail
          ) {
            return (
              <MarketTableItemBond
                key={asset.code}
                {...itemProps}
                currencyCode={asset.code}
                actions={actions}
                data={asset.data.bondMarketDetail}
                showYieldColumn={showYieldColumn}
                showCouponColumn={showCouponColumn}
              />
            );
          }

          if (
            activeAssetType === API.MarketAssetType.Fund &&
            asset.data?.fundMarketDetail
          ) {
            return (
              <MarketTableItemFund
                key={asset.code}
                {...itemProps}
                currencyCode={asset.code}
                name={asset.valueAsOf}
                displayCode={asset.name}
                data={asset.data.fundMarketDetail}
              />
            );
          }

          if (
            activeAssetType === API.MarketAssetType.Crypto &&
            asset.data?.cryptoMarketDetail
          ) {
            return (
              <MarketTableItemCrypto
                key={asset.code}
                {...itemProps}
                currencyCode={asset.code}
                actions={actions}
                data={asset.data.cryptoMarketDetail}
              />
            );
          }

          return null;
        })}
      </>
    );
  }
);

type TableDetailsProps = Omit<
  UseMarketType,
  | 'columns'
  | 'onPageChanged'
  | 'onSortBy'
  | 'assetTypes'
  | 'onAssetTypeChanged'
  | 'error'
> & {
  applyParams: (
    value: QueryParams,
    callBack?: (params: URLSearchParams) => void
  ) => void;
  header?: JSX.Element;
  onPressPage: (page: number) => void;
  showYieldColumn?: boolean;
  showCouponColumn?: boolean;
} & GetBuildEnvType;

const TableDetails = ({
  isLoading,
  activeAssetType,
  isActiveAssetOfTypeFund,
  categoriesByAssetType,
  isActiveAssetCategory,
  onSearchChanged,
  onPressPage,
  data,
  hasNoData,
  header,
  showPagination,
  showCategoriesAndSearch,
  onFiltersChanged,
  search,
  showCouponColumn,
  showYieldColumn,
  getBuildEnv,
}: TableDetailsProps) => {
  const showFundsExperience = useExperience();
  return (
    <div className="overflow-x-auto">
      {showCategoriesAndSearch && (
        <DashboardHeader withBorder title="">
          <div className="p-4 lg:p-6">
            <div className="flex whitespace-nowrap overflow-auto outline scrollbar-hide gap-2">
              {categoriesByAssetType[activeAssetType]?.map(
                ({ id, label, filters }, index) => (
                  <TabItemSwitcher
                    key={id}
                    active={isActiveAssetCategory(filters)}
                    index={index}
                    onPress={() => {
                      onFiltersChanged({
                        ...filters,
                        search: '',
                        categories:
                          id === 'all' ? [] : [id as API.MarketAssetCategory],
                      });
                    }}
                    small
                    title={startCase(label)}
                  />
                )
              )}
              <span className="ml-auto">
                <SearchInput
                  onChange={onSearchChanged}
                  collapsed={false}
                  maxLength={500}
                  value={search}
                />
              </span>
            </div>
          </div>
        </DashboardHeader>
      )}
      <div className="bg-white">
        <Table
          empty={hasNoData}
          loading={isLoading}
          rounded={!showCategoriesAndSearch}
        >
          {header}
          <TableRows
            activeAssetType={activeAssetType}
            isLoading={isLoading}
            data={data}
            hasNoData={hasNoData}
            showCouponColumn={showCouponColumn}
            showYieldColumn={showYieldColumn}
            getBuildEnv={getBuildEnv}
          />
        </Table>
      </div>
      {isActiveAssetOfTypeFund && !hasNoData && !isLoading && (
        <div
          className={cx(
            showFundsExperience ? 'bg-white' : 'bg-grey-brighter',
            'pt-3 text-grey-darker text-xs'
          )}
        >
          ¹Performance metrics based on actual realized returns.
        </div>
      )}

      {showPagination && !!data && (
        <div className="mt-6">
          <Pagination
            {...data.pagination}
            totalPages={isLoading ? 0 : data.pagination.totalPages}
            onPressPage={onPressPage}
          />
        </div>
      )}
    </div>
  );
};

export const ListOfAssets = () => {
  const { REACT_APP_BUILD_ENV } = useEnvironmentConfig();
  const { isLowerRegionEnv } = getRuntimeEnv(REACT_APP_BUILD_ENV as BuildEnv);

  /**
   * Hooks
   */
  const {
    error,
    assetTypes,
    onAssetTypeChanged,
    data,
    onPageChanged: handlePressPage,
    onSortBy,
    ...rest
  } = useMarkets(DataStore);
  const [forceInitialTabIndex, setForceInitialTabIndex] = useState(0);

  const { applyParams, queryParams } = useUrlParams({
    [QUERY_PARAMS.ASSET_CATEGORY]: 'all',
    [QUERY_PARAMS.COLUMN]: MarketsColumn.Asset,
    [QUERY_PARAMS.DIRECTION]: API.SortDirection.Ascending,
  });
  const { breakpoints } = useBreakpoints();
  const assetTypeName = queryParams.get(QUERY_PARAMS.ASSET_TYPE);
  const assetCategory = queryParams.get(QUERY_PARAMS.ASSET_CATEGORY);
  const direction = queryParams.get(QUERY_PARAMS.DIRECTION);
  const column = queryParams.get(QUERY_PARAMS.COLUMN);

  const assetType = assetTypes?.find(id => id.displayName === assetTypeName);
  const initialTabIndex = assetType ? assetTypes.indexOf(assetType) : 0;

  const handleChangeTab = useCallback(
    (type: string) => {
      onAssetTypeChanged(type as API.MarketAssetType);

      const _assetType = assetTypes.find(item => item.type === type);
      if (_assetType) {
        applyParams({ [QUERY_PARAMS.ASSET_TYPE]: _assetType.displayName });
      }
    },
    [applyParams, assetTypes, onAssetTypeChanged]
  );

  useEffectOnce(() => {
    const _assetType =
      (assetType?.type as API.MarketAssetType) ||
      rest.activeAssetType ||
      API.MarketAssetType.Crypto;
    const _categories = rest.categoriesByAssetType[_assetType] ?? [];
    const _category =
      _categories.find(({ id }) => id === (assetCategory || 'all')) ?? null;
    const _filters =
      _category?.filters ||
      (_assetType && rest.filters?.types[0] === _assetType
        ? rest.filters
        : ({} as RemoveNullable<API.MarketListAssetsRequest>));

    _filters.types = [_assetType];
    _filters.sortColumn =
      API.MarketListAssetsSortType[column as string] || _filters.sortColumn;
    _filters.sortDirection =
      API.SortDirection[direction as string] || _filters.sortDirection;

    rest.onFiltersChanged(_filters);
  });

  // This must not trigger on inital render, only
  useUpdateEffect(() => {
    if (!assetType) {
      return;
    }
    onAssetTypeChanged(assetType.type);
    setForceInitialTabIndex(Math.random());
  }, [assetType]);

  useEffect(() => {
    applyParams({
      [QUERY_PARAMS.ASSET_CATEGORY]: rest.filters?.categories?.length
        ? rest.filters?.categories
        : undefined,
      [QUERY_PARAMS.COLUMN]: rest.filters?.sortColumn,
      [QUERY_PARAMS.DIRECTION]: rest.filters?.sortDirection,
    });
  }, [
    rest.filters?.sortColumn,
    rest.filters?.sortDirection,
    rest.filters?.categories,
    applyParams,
  ]);

  useEffectOnce(() => {
    return () => {
      onAssetTypeChanged(API.MarketAssetType.Crypto, true);
    };
  });

  /**
   * DOM
   */
  const showBondsYieldColumn = !!data?.rows
    ?.map(row => {
      return row.data?.bondMarketDetail?.yieldPercent;
    })
    .find(item => item?.gt(0));

  const showBondsCouponColumn = !!data?.rows
    ?.map(row => {
      return row.data?.bondMarketDetail?.couponPercent;
    })
    .find(item => item?.gt(0));

  const header = breakpoints.LESS_THAN_S ? (
    <MarketListHeader
      sortId={rest.filters.sortColumn}
      sortDir={rest.filters.sortDirection}
      assetType={rest.activeAssetType}
      onSort={onSortBy}
      variant="web"
    />
  ) : (
    <MarketTableHeader
      sortId={rest.filters.sortColumn}
      sortDir={rest.filters.sortDirection}
      assetType={rest.activeAssetType}
      showCouponColumn={showBondsCouponColumn}
      showYieldColumn={showBondsYieldColumn}
      onSort={onSortBy}
    />
  );

  return (
    <Container>
      <Tabs
        initialIndex={initialTabIndex}
        forceInitialTabIndex={forceInitialTabIndex}
        tabs={assetTypes.map(item => ({
          key: item.type,
          title: item.displayName,
        }))}
        onTabChange={handleChangeTab}
        panels={
          error
            ? []
            : assetTypes?.map(item => (
                <TableDetails
                  data={data}
                  key={item.type}
                  {...rest}
                  applyParams={applyParams}
                  onPressPage={handlePressPage}
                  showCouponColumn={showBondsCouponColumn}
                  showYieldColumn={showBondsYieldColumn}
                  header={header}
                  getBuildEnv={() => REACT_APP_BUILD_ENV}
                />
              ))
        }
      />
      {error && (
        <GenericError
          error={isLowerRegionEnv ? new Error(error) : undefined}
          containerCls="flex-1"
        />
      )}
    </Container>
  );
};

const Container = ({ children }: PropsWithChildren) => {
  const showFundsExperience = useExperience();

  return showFundsExperience ? (
    <PageContainer>{children}</PageContainer>
  ) : (
    <AppContainer
      bgColor
      fullHeight
      containerWidth="lg"
      containerCls="flex flex-col"
      cls="h-full pb-10 px-10"
    >
      {children}
    </AppContainer>
  );
};
